import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Clipboard } from '@angular/cdk/clipboard';
import { finalize, forkJoin, Observable } from 'rxjs';
import { TranslationManagerService } from '../../../core/i18n';
import { ICardV2 } from '../../../interfaces/card';
import { BoardNovaPoshtaService } from '../../../services/board-nova-poshta.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import {
  NovaPoshtaLinkFormPayload,
  NovaPoshtaShipmentFormPayload,
  SidebarType,
} from '../../layout/stacked-sidebar/stacked-sidebar.interface';
import { NpCabinet, NpPrintFormat, NpShipment, NpShipmentPrefill } from '../../nova-poshta/nova-poshta.types';
import {
  formatNpPhone,
  formatTtn,
  NP_CARGO_TYPES,
  NP_DESCRIPTION_MAX_LENGTH,
  NP_PRINT_FORMATS,
  NP_SHIPMENT_STATUS_STYLES,
} from '../../nova-poshta/nova-poshta.utils';
import { formatMoneyAmount } from '../price-field.utils';

/**
 * The Nova Poshta tab of a deal: its waybills, a new one from the deal data or an existing one linked. Shown once the
 * Nova Poshta application is installed; without a cabinet it leads to the settings. The backend keeps the
 * `ttn_number` field of the deal and the card update comes by the socket.
 */
@Component({
  selector: 'app-board-v3-card-nova-poshta',
  templateUrl: './card-nova-poshta.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardNovaPoshtaComponent implements OnInit {
  @Input({ required: true }) cardId!: string;
  /** The deal: the recipient and the amounts of a new waybill come from it */
  @Input({ required: true }) card!: ICardV2;

  private readonly npService = inject(BoardNovaPoshtaService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly translationManager = inject(TranslationManagerService);
  private readonly clipboard = inject(Clipboard);
  private readonly destroyRef = inject(DestroyRef);

  protected cabinets: NpCabinet[] = [];
  protected shipments: NpShipment[] = [];
  protected loading = true;
  protected busyId: string | null = null;
  protected copiedId: string | null = null;
  private copiedTimer?: ReturnType<typeof setTimeout>;

  protected readonly statusStyles = NP_SHIPMENT_STATUS_STYLES;
  protected readonly printFormats = NP_PRINT_FORMATS;
  protected readonly cargoIcons = Object.fromEntries(NP_CARGO_TYPES.map((type) => [type.value, type.icon]));
  protected readonly formatTtn = formatTtn;
  protected readonly formatNpPhone = formatNpPhone;
  protected readonly formatMoneyAmount = formatMoneyAmount;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => clearTimeout(this.copiedTimer));
    // The card opens over any screen, not only over the ones whose route loads the Nova Poshta texts
    this.translationManager.loadModuleTranslations('novaposhta').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.load();
  }

  protected openForm(): void {
    const payload: NovaPoshtaShipmentFormPayload = { cardId: this.cardId, prefill: this.prefill() };
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.NovaPoshtaShipmentForm, width: '640px' }, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shipment?: NpShipment) => shipment && this.add(shipment));
  }

  protected openLinkForm(): void {
    const payload: NovaPoshtaLinkFormPayload = { cardId: this.cardId };
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.NovaPoshtaLinkForm, width: '560px' }, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shipment?: NpShipment) => shipment && this.add(shipment));
  }

  protected openSettings(): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.NovaPoshtaSettings, width: '560px' }, {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cabinets?: NpCabinet[]) => {
        if (cabinets) this.cabinets = cabinets;
      });
  }

  protected copy(shipment: NpShipment): void {
    if (!this.clipboard.copy(shipment.ttn_number)) return;
    this.copiedId = shipment.id;
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => (this.copiedId = null), 1500);
  }

  protected trackingUrl(shipment: NpShipment): string {
    return this.npService.trackingUrl(shipment.ttn_number);
  }

  protected print(shipment: NpShipment, format: NpPrintFormat): void {
    this.npService.print(shipment, format);
  }

  /** Deletes the waybill in Nova Poshta: only one that is not sent yet */
  protected remove(shipment: NpShipment): void {
    this.npService
      .confirm('novaposhta.shipments.delete_confirm', { ttn: formatTtn(shipment.ttn_number) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.drop(shipment, this.npService.deleteShipment(shipment.id), 'novaposhta.shipments.deleted'));
  }

  /** Takes the waybill out of the deal; it stays in Nova Poshta */
  protected unlink(shipment: NpShipment): void {
    this.npService
      .confirm('novaposhta.shipments.unlink_confirm', { ttn: formatTtn(shipment.ttn_number) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.drop(shipment, this.npService.unlinkShipment(shipment.id), 'novaposhta.shipments.unlinked'));
  }

  /** Linking the same waybill again returns the one the deal has */
  private add(shipment: NpShipment): void {
    this.shipments = [shipment, ...this.shipments.filter((item) => item.id !== shipment.id)];
  }

  private drop(shipment: NpShipment, change$: Observable<void>, successKey: string): void {
    this.busyId = shipment.id;
    change$.pipe(finalize(() => (this.busyId = null))).subscribe({
      next: () => {
        this.shipments = this.shipments.filter((item) => item.id !== shipment.id);
        this.npService.success(successKey);
      },
      error: (error) => this.npService.error(error),
    });
  }

  private load(): void {
    forkJoin({ cabinets: this.npService.getCabinets(), shipments: this.npService.getCardShipments(this.cardId) })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: ({ cabinets, shipments }) => {
          this.cabinets = cabinets;
          this.shipments = shipments;
        },
        error: (error) => this.npService.error(error),
      });
  }

  /** The first contact of the deal, else the deal itself; the amounts only in hryvnias */
  private prefill(): NpShipmentPrefill {
    const card = this.card;
    const contact = card.contacts?.[0];
    const contactPhone = contact?.phones.find((phone) => phone.is_primary) ?? contact?.phones[0];
    const cardPhone = card.phones?.[0];
    const address = contact?.addresses?.[0] ?? card.addresses?.[0];
    const items = (card.items ?? []).map((item) => item.variant?.product_name).filter(Boolean);
    const isUah = String(card.currency) === 'UAH';
    return {
      recipientName: contact
        ? [contact.last_name, contact.first_name, contact.middle_name].filter(Boolean).join(' ')
        : '',
      recipientPhone: contactPhone?.number_e164 ?? cardPhone?.number_e164 ?? cardPhone?.phone ?? '',
      cityName: address?.city ?? null,
      description: items.join(', ').slice(0, NP_DESCRIPTION_MAX_LENGTH),
      amount: isUah && card.price ? card.price : null,
    };
  }
}
